import React, {createContext, useContext, useState} from 'react';
import * as actions from '../actions/actions';
import {useFormik} from "formik";
import moment from "moment";

const emptyNote = {
  generalNoteID: 0,
  shumaID: 0,
  responsibleUserID: 0,
  createDate: new Date(),
  statusTypeID: 0,
  statusTypeName: '',
  dueDate: new Date(),
  freeText: '',
};

const PopupNotesContext = createContext({
  list: [],
  details: emptyNote,
  loading: false,
  updateList: () => {
  },
  getDetails: () => {
  },
  resetDetails: () => {
  },
  createNote: () => {
  },
  editNote: () => {
  },
  deleteNote: () => {
  },
});

export const usePopupNotesContext = () => useContext(PopupNotesContext);

export const PopupNotesContextProvider = ({children, shumaID}) => {
  const [list, setList] = useState([]);
  const [details, setDetails] = useState(emptyNote);
  const [loading, setLoading] = useState(false);

  const updateList = async () => {
    setLoading(true);
    try {
      const list = await actions.getList(shumaID);
      setList([...list]);
    } catch (e) {
      console.error(e);
      setList([]);
    } finally {
      setLoading(false)
    }
  };

  const getDetails = async (id) => {
    if (id !== -1) {
      try {
        const data = await actions.getDetails(id);
        setDetails(data);
      } catch (e) {
        console.error(e);
        setDetails(emptyNote);
      }
    }
  };

  const resetDetails = (id) => {
    if (id === -1) {
      setDetails(emptyNote);
    }
  };

  const createNote = async (
    statusTypeID,
    dueDate,
    responsibleUserID,
    freeText
  ) => {
    try {
      await actions.addEditNote({
        generalNoteID: -1,
        shumaID,
        statusTypeID,
        dueDate,
        responsibleUserID,
        freeText
      });
    } catch (e) {
      console.error(e);
    }
  };

  const editNote = async (
    generalNoteID,
    statusTypeID,
    dueDate,
    responsibleUserID,
    freeText
  ) => {
    try {
      await actions.addEditNote({
        generalNoteID,
        shumaID,
        statusTypeID,
        dueDate,
        responsibleUserID,
        freeText
      });
    } catch (e) {
      console.error(e);
    }
  };

  const deleteNote = async (id) => {
    try {
      await actions.deleteNote(id);
    } catch (e) {
      console.error(e);
    }
  };

  const fieldNames = {
    responsibleUserID: 'responsibleUserID',
    dueDate: 'dueDate',
    statusTypeID: 'statusTypeID',
    freeText: 'freeText',
  };

  const formik = useFormik({
    initialValues: {
      generalNoteID: details?.generalNoteID || -1,
      responsibleUserID: details?.responsibleUserID,
      dueDate: details?.dueDate && details.generalNoteID ? moment(details.dueDate).toDate() : null,
      statusTypeID: details?.statusTypeID,
      freeText: details?.freeText || '',
    },
    onSubmit: () => {
    },
    validate: (values) => {
      let errors = {};
      if (!values.statusTypeID) errors['statusTypeID'] = 'Required field';
      return errors;
    },
    validateOnMount: true,
    enableReinitialize: true,
  });

  const [noteStatusTypes, setNoteStatusTypes] = useState([]);
  const getNoteOptions = () => {
    actions.getNoteStatusTypes()
      .then(setNoteStatusTypes)
      .catch((e) => {
        console.error(e);
        setNoteStatusTypes([]);
      });
  };

  const resultList = [
    ...list.map((item) => {
      return {
        ...item,
        onOpen: async () => {
          formik.resetForm();
          await getDetails(item.generalNoteID);
        },
        onEdit: async () => {
          await editNote(
            item.generalNoteID,
            formik.values.statusTypeID,
            formik.values.dueDate,
            formik.values.responsibleUserID,
            formik.values.freeText
          );
          await updateList();
        },
        onDelete: async () => {
          await deleteNote(item.generalNoteID);
          await updateList();
        }
      };
    })
  ];

  const resetNoteDetails = () => {
    formik.setFieldValue('generalNoteID', -1);
    formik.setFieldValue('responsibleUserID', null);
    formik.setFieldValue('dueDate', null);
    formik.setFieldValue('statusTypeID', null);
    formik.setFieldValue('freeText', '');
  }

  return (
    <PopupNotesContext.Provider value={{
      list: resultList,
      details,
      loading,
      updateList,
      getDetails,
      resetDetails,
      createNote,
      editNote,
      deleteNote,
      formik,
      fieldNames,
      disabled: !Boolean(shumaID) || (shumaID === -1),
      noteStatusTypes,
      shumaID,
      resetNoteDetails,
      getNoteOptions
    }}>
      {children}
    </PopupNotesContext.Provider>
  );
};